import { fetchWithAuth } from '../helpers';
import { getApiUrl } from '../config/api';

// Helper function to handle response
const handleResponse = async (response: Response) => {
  const data = await response.json();
  if (!response.ok) {
    const error = (data && data.message) || response.statusText;
    return Promise.reject(error);
  }
  return data;
};

// Tuition Invoice API
const tuitionInvoiceService = {
  // Get all invoices with optional filters
  getAll: async (filters?: { month?: number; year?: number; studentId?: number; paymentMethod?: string }) => {
    const queryParams = new URLSearchParams();
    if (filters?.month) queryParams.append('month', filters.month.toString());
    if (filters?.year) queryParams.append('year', filters.year.toString());
    if (filters?.studentId) queryParams.append('studentId', filters.studentId.toString());
    if (filters?.paymentMethod) queryParams.append('paymentMethod', filters.paymentMethod);

    const url = `${getApiUrl()}/api/tuition-invoices/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetchWithAuth(url, {
      method: 'GET',
      headers: { "ngrok-skip-browser-warning": "69420" },
    });
    return handleResponse(response);
  },

  // Get invoice by ID
  getById: async (id: number) => {
    const response = await fetchWithAuth(`${getApiUrl()}/api/tuition-invoices/${id}`, {
      method: 'GET',
      headers: { "ngrok-skip-browser-warning": "69420" },
    });
    return handleResponse(response);
  },

  // Get invoices by tuition detail ID
  getByTuitionDetailId: async (tuitionDetailId: number) => {
    const response = await fetchWithAuth(`${getApiUrl()}/api/tuition-invoices/tuition-detail/${tuitionDetailId}`, {
      method: 'GET',
      headers: { "ngrok-skip-browser-warning": "69420" },
    });
    return handleResponse(response);
  },

  // Create invoice
  create: async (invoice: any) => {
    const response = await fetchWithAuth(`${getApiUrl()}/api/tuition-invoices/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        "ngrok-skip-browser-warning": "69420",
      },
      body: JSON.stringify(invoice),
    });
    return handleResponse(response);
  },

  // Update invoice
  update: async (id: number, invoice: any) => {
    const response = await fetchWithAuth(`${getApiUrl()}/api/tuition-invoices/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        "ngrok-skip-browser-warning": "69420",
      },
      body: JSON.stringify(invoice),
    });
    return handleResponse(response);
  },

  // Delete invoice
  delete: async (id: number) => {
    const response = await fetchWithAuth(`${getApiUrl()}/api/tuition-invoices/${id}`, {
      method: 'DELETE',
      headers: { "ngrok-skip-browser-warning": "69420" },
    });
    return handleResponse(response);
  },

  // Get invoice statistics
  getStatistics: async (month: number, year: number) => {
    const queryParams = new URLSearchParams();
    queryParams.append('month', month.toString());
    queryParams.append('year', year.toString());

    const url = `${getApiUrl()}/api/tuition-invoices/statistics?${queryParams.toString()}`;
    const response = await fetchWithAuth(url, {
      method: 'GET',
      headers: { "ngrok-skip-browser-warning": "69420" },
    });
    return handleResponse(response);
  },

  // Upload transfer image
  uploadTransferImage: async (file: File) => {
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetchWithAuth(`${getApiUrl()}/api/upload/transfer-image`, {
      method: 'POST',
      headers: { "ngrok-skip-browser-warning": "69420" },
      body: formData,
    });
    return handleResponse(response);
  },

  // Download invoice PDF
  downloadPDF: async (id: number) => {
    const response = await fetchWithAuth(`${getApiUrl()}/api/tuition-invoices/${id}/pdf`, {
      method: 'GET',
      headers: { "ngrok-skip-browser-warning": "69420" },
    });
    
    if (!response.ok) {
      throw new Error('Failed to download PDF');
    }
    
    // Get blob from response
    const blob = await response.blob();
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoice-${id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return { success: true };
  },

  // Get invoices by multiple tuition detail IDs (batch)
  getByTuitionDetailIds: async (tuitionDetailIds: number[]) => {
    const response = await fetchWithAuth(`${getApiUrl()}/api/tuition-invoices/batch-by-tuition-details`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        "ngrok-skip-browser-warning": "69420",
      },
      body: JSON.stringify({ tuitionDetailIds }),
    });
    return handleResponse(response);
  },
};

export default tuitionInvoiceService;
